import { useApi } from 'api'
import { Invoice } from 'types'
import { useEffect, useCallback, useState } from 'react'
import { getInvoiceTaxes, getInvoiceTotal } from './invoice_total'
import FinalizeButton from './FinalizeButton'
import DeleteButton from './DeleteButton'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Stack } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'

import numeral from 'numeral'

const InvoicesList = (): React.ReactElement => {
  const api = useApi()
  const navigate = useNavigate()

  const [invoicesList, setInvoicesList] = useState<Invoice[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)

  const fetchInvoices = useCallback(
    async (page = 1) => {
      const {
        data: { invoices, pagination = { page, total_pages: page } },
      } = await api.getInvoices({ page })
      if (pagination.page < pagination.total_pages) {
        setCurrentPage(pagination.page)
      } else {
        setCurrentPage(Infinity)
      }
      setInvoicesList(invoices)
    },
    [api]
  )

  const deleteInvoice = async function (invoice: Invoice) {
    const updatedList = invoicesList.filter((i) => invoice.id !== i.id)
    setInvoicesList(updatedList)
  }

  const finalizeInvoice = async function (invoice: Invoice) {
    const updatedList = invoicesList.map((i) =>
      invoice.id === i.id ? invoice : i
    )
    setInvoicesList(updatedList)
  }

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  return (
    <div className="col">
      <div className="row mb-3">
        <h2 className="col">Invoices</h2>
        <Button onClick={() => navigate('/invoice/new')} className="col-md-1">
          <FontAwesomeIcon icon={faPlusCircle}></FontAwesomeIcon> Create
        </Button>
      </div>
      <div className="row">
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              <th>Id</th>
              <th>Customer</th>
              <th>Address</th>
              <th>Total</th>
              <th>Tax</th>
              <th>Finalized</th>
              <th>Paid</th>
              <th>Date</th>
              <th>Deadline</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoicesList.map((invoice) => (
              <tr key={invoice.id}>
                <td>
                  <Link to={`/invoice/${invoice.id}`}>{invoice.id}</Link>
                </td>
                <td>
                  {invoice.customer?.first_name} {invoice.customer?.last_name}
                </td>
                <td>
                  {invoice.customer?.address}, {invoice.customer?.zip_code}{' '}
                  {invoice.customer?.city}
                </td>
                <td>{numeral(getInvoiceTotal(invoice)).format('$0.00')}</td>
                <td>{numeral(getInvoiceTaxes(invoice)).format('$0.00')}</td>
                <td>{invoice.finalized ? 'Yes' : 'No'}</td>
                <td>{invoice.paid ? 'Yes' : 'No'}</td>
                <td className="text-nowrap">{invoice.date}</td>
                <td className="text-nowrap">{invoice.deadline}</td>
                <td className="text-nowrap" style={{ width: '1px' }}>
                  <Stack direction="horizontal" gap={3}>
                    <FinalizeButton
                      invoice={invoice}
                      onFinalize={finalizeInvoice}
                    ></FinalizeButton>
                    <div className="vr" />
                    <DeleteButton
                      invoice={invoice}
                      onDelete={deleteInvoice}
                    ></DeleteButton>
                  </Stack>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="row">
        <div className="col d-grid">
          <Button
            variant={
              currentPage === 1 ? 'outline-secondary' : 'outline-primary'
            }
            disabled={currentPage === 1}
            onClick={() => fetchInvoices(currentPage - 1)}
          >
            Previous page
          </Button>
        </div>
        <div className="col d-grid">
          <Button
            variant={
              currentPage === Infinity ? 'outline-secondary' : 'outline-primary'
            }
            disabled={currentPage === Infinity}
            onClick={() => fetchInvoices(currentPage + 1)}
          >
            Next page
          </Button>
        </div>
      </div>
    </div>
  )
}

export default InvoicesList
